import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, makeStyles } from '@material-ui/core';
import ImageIcon from '@material-ui/icons/Image';
import { DateTime } from 'luxon';
import { CommonButton, StaticBarcodes } from './CommonComponents';
import { CouponMasterItemTableRow } from '../../../../types/couponPackMaster';
import { HtmlText } from '@sbgift-coupon/use';

type PackCompletedSmartCheckTemplateProps = {
  setStatus: React.Dispatch<React.SetStateAction<string>>;
  currentCouponSelected?: CouponMasterItemTableRow;
  usedQuantity?: number;
  codeInput?: string;
};

const useStyles = makeStyles((theme) => ({
  container: {
    height: 'fit-content',
    backgroundColor: 'white',
    padding: '0 20px 28px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  couponImageWrapper: {
    marginBottom: theme.spacing(3),
    position: 'relative',
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'center',
  },
  imageStack: {
    position: 'relative',
    display: 'inline-block',
    lineHeight: 0,
  },
  couponImage: {
    maxWidth: '100%',
    height: 'auto',
    width: 'auto',
    display: 'block',
  },
  completedCircle: {
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    border: '8px solid white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
  },
  completedText: {
    fontSize: 80,
    fontWeight: 600,
    color: '#FFFFFF',
    fontFamily: 'Hiragino Kaku Gothic ProN',
  },
  couponTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: '#303030',
    textAlign: 'left',
    marginBottom: '16px',
    lineHeight: '160%',
    maxWidth: 362,
    wordBreak: 'break-all',
    marginLeft: '8px',
  },
  remainingTime: {
    fontSize: 18,
    fontWeight: 600,
    color: '#FF3B30',
    textAlign: 'center',
    marginBottom: '16px',
    lineHeight: '160%',
    maxWidth: 362,
    wordBreak: 'break-all',
  },
  infoBox: {
    width: '100%',
    maxWidth: 362,
    borderRadius: 8,
    marginBottom: theme.spacing(3),
  },
  infoRow: {
    display: 'flex',
    marginBottom: '20px',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  infoLabel: {
    fontSize: 16,
    fontWeight: 600,
    color: '#303030',
    minWidth: '70px',
    marginRight: '8px',
    display: 'flex',
    justifyContent: 'space-between',
  },
  infoValue: {
    fontSize: 16,
    fontWeight: 600,
    color: '#FF3B30',
  },
  warningText: {
    fontSize: 12,
    fontWeight: 300,
    color: '#FF3B30',
    marginBottom: theme.spacing(3),
    lineHeight: '160%',
    maxWidth: 362,
    textAlign: 'left',
    width: '100%',
  },
  buttonWrapper: {
    width: '100%',
    textAlign: 'center',
  },
  imageDefault: {
    width: '280px',
    height: '280px',
    color: '#d3d3d3',
  },
  successOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    objectFit: 'cover',
  },
}));

export const PackCompletedSmartCheckTemplate: React.FC<PackCompletedSmartCheckTemplateProps> = (props) => {
  const { setStatus, currentCouponSelected, usedQuantity = 1, codeInput } = props;
  const classes = useStyles();
  const [usedAt] = useState<string>(DateTime.now().toISO() ?? '');
  const [intervalDuration, setIntervalDuration] = useState(-1);
  const timerDisplayRef = useRef<HTMLSpanElement>(null);
  const [shouldRunCountdown, setShouldRunCountdown] = useState(false);

  // Initialize countdown timer
  useEffect(() => {
    const countDownMinutes = Number(currentCouponSelected?.smartCheckSetting?.countDownMinute ?? 0);
    if (countDownMinutes > 0 && usedAt) {
      const usedAtTime = DateTime.fromISO(usedAt);
      const endTime = usedAtTime.plus({ minutes: countDownMinutes });
      const now = DateTime.now();
      const remainingSeconds = Math.floor(endTime.diff(now, 'seconds').seconds);

      if (remainingSeconds <= 0) {
        setIntervalDuration(0);
        setShouldRunCountdown(false);
      } else {
        setIntervalDuration(remainingSeconds);
        setShouldRunCountdown(true);
      }
    } else {
      setIntervalDuration(countDownMinutes * 60);
      setShouldRunCountdown(false);
    }
  }, [currentCouponSelected, usedAt]);

  // Determine if countdown should be shown
  const shouldShowCountdown = useMemo(() => {
    if (!currentCouponSelected?.smartCheckSetting?.countDownMinute || !usedAt) {
      return false;
    }

    const countDownMinutes = Number(currentCouponSelected.smartCheckSetting.countDownMinute);
    const usedAtTime = DateTime.fromISO(usedAt);
    const endTime = usedAtTime.plus({ minutes: countDownMinutes });
    const now = DateTime.now();

    return now < endTime;
  }, [currentCouponSelected?.smartCheckSetting?.countDownMinute, usedAt]);

  // Countdown timer effect
  useEffect(() => {
    if (!currentCouponSelected?.smartCheckSetting?.countDownMinute || !usedAt || !shouldRunCountdown) return;

    const timer = setInterval(() => {
      const countDownMinutes = Number(currentCouponSelected?.smartCheckSetting?.countDownMinute);
      const usedAtTime = DateTime.fromISO(usedAt);
      const endTime = usedAtTime.plus({ minutes: countDownMinutes });
      const now = DateTime.now();
      const remainingSeconds = Math.floor(endTime.diff(now, 'seconds').seconds);

      if (remainingSeconds <= 0) {
        clearInterval(timer);
        setIntervalDuration(0);
        if (timerDisplayRef.current) {
          timerDisplayRef.current.textContent = `残り時間: 0分0秒`;
        }
        setStatus('LIST');
        return;
      }

      setIntervalDuration(remainingSeconds);

      if (timerDisplayRef.current) {
        const minutes = Math.floor(remainingSeconds / 60);
        const secs = remainingSeconds % 60;
        timerDisplayRef.current.textContent = `残り時間: ${minutes}分${secs}秒`;
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [currentCouponSelected?.smartCheckSetting?.countDownMinute, usedAt, shouldRunCountdown, setStatus]);

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}分${secs}秒`;
  };

  const formatJstDateTime = (dateTime: string): string => {
    return DateTime.fromISO(dateTime).toFormat('yyyy年MM月dd日 HH:mm');
  };

  const handleTopClick = (): void => {
    setStatus('LIST');
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.couponImageWrapper}>
        {currentCouponSelected?.smartCheckImageUrl?.smartCheckUsedImageUrl ? (
          <img
            src={currentCouponSelected.smartCheckImageUrl.smartCheckUsedImageUrl}
            alt="Smart Check Used Image"
            className={classes.couponImage}
          />
        ) : (
          <>
            <Box className={classes.imageStack}>
              {currentCouponSelected?.imageUrl ? (
                <img src={currentCouponSelected?.imageUrl} alt="クーポン" className={classes.couponImage} />
              ) : (
                <ImageIcon className={classes.imageDefault} />
              )}
              <img src="/success_image.png" alt="Coupon" className={classes.successOverlay} />
            </Box>
          </>
        )}
      </Box>

      {shouldShowCountdown && intervalDuration >= 0 && (
        <>
          <Typography className={classes.remainingTime} ref={timerDisplayRef}>
            残り時間: {formatTime(intervalDuration)}
          </Typography>

          {currentCouponSelected?.smartCheckSetting?.smartCheckCountDownText ? (
            <Box marginBottom={2} textAlign="center">
              <HtmlText text={currentCouponSelected?.smartCheckSetting.smartCheckCountDownText} />
            </Box>
          ) : (
            <></>
          )}
        </>
      )}

      <Typography className={classes.couponTitle}>
        {currentCouponSelected?.couponNameHtml
          ? currentCouponSelected?.couponNameHtml
          : currentCouponSelected?.couponMasterName}
      </Typography>

      <Box className={classes.infoBox}>
        <Box className={classes.infoRow}>
          <Typography className={classes.infoLabel}>
            利用枚数<span>:</span>
          </Typography>
          <Typography className={classes.infoValue}>{usedQuantity}枚</Typography>
        </Box>

        {!!currentCouponSelected?.couponAmount && (
          <Box className={classes.infoRow}>
            <Typography className={classes.infoLabel}>
              利用金額<span>:</span>
            </Typography>
            <Typography className={classes.infoValue}>
              {(currentCouponSelected.couponAmount * usedQuantity).toLocaleString('en-US')} <span>円</span>
            </Typography>
          </Box>
        )}

        <Box className={classes.infoRow}>
          <Typography className={classes.infoLabel}>
            利用店舗<span>:</span>
          </Typography>
        </Box>

        <Box className={classes.infoRow}>
          <Typography className={classes.infoLabel}>
            利用日<span>:</span>
          </Typography>
          <Typography className={classes.infoValue}>{formatJstDateTime(usedAt)}</Typography>
        </Box>
      </Box>

      {(intervalDuration > 0 || !currentCouponSelected?.smartCheckSetting?.countDownMinute) &&
      currentCouponSelected?.smartCheckSetting?.staticBarcodes ? (
        <StaticBarcodes
          staticBarcodes={currentCouponSelected?.smartCheckSetting?.staticBarcodes
            .filter((b): b is typeof b & { barcode: string } => typeof b.barcode === 'string')
            .map((b) => ({ ...b, barcode: b.barcode }))}
          imageServer={currentCouponSelected?.imageServer || 'https://sbg.jp'}
        />
      ) : (
        <></>
      )}

      <Typography className={classes.warningText}>※スタッフは利用内容を適用してください</Typography>

      <Box className={classes.buttonWrapper}>
        <CommonButton onClick={handleTopClick} label="TOPに戻る" variant="secondary" fullWidth />
      </Box>
    </Box>
  );
};
